/**
 * The asset editor (`/assets/:assetId/edit`, `asset.manage`): the asset's fields, tags and experts with a save bar;
 * one card per section, each saving on its own; and the related assets. Every save answers the next revision, so
 * the page keeps saving without a reload. When someone else saved first (409), a notice asks for a reload; drafts
 * not saved yet stay in their fields.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';

import { isStaleRevision, useAsset, useUpdateAsset } from '@/api/queries';
import type { AssetOut } from '@/api/types';
import { bannerError, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { buttonState } from '@/ui/ActionButton';
import { PageHead } from '@/ui/PageHead';
import { SaveBar } from '@/ui/SaveBar';
import { FormError, LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';

import { AssetFieldsForm, FIELD_PATHS } from '../fields/AssetFieldsForm';
import { changedFields, fieldsOf, fieldsValid, type AssetFieldsDraft } from '../fields/assetFields';
import { StaleNotice } from '../StaleNotice';
import { RelationsEditor } from './RelationsEditor';
import { SectionsEditor } from './SectionsEditor';

function FieldsCard({ asset, onStale }: { asset: AssetOut; onStale: () => void }) {
  const update = useUpdateAsset(asset.id);
  const initial = fieldsOf(asset);
  const [draft, setDraft] = useState<AssetFieldsDraft>(initial);
  const patch = changedFields(initial, draft);
  const changed = Object.keys(patch).length > 0;

  return (
    <form
      className="card asset-form"
      aria-labelledby="editor-fields-title"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { revision: asset.revision, ...patch },
          { onSuccess: (saved) => setDraft(fieldsOf(saved)), onError: (error) => isStaleRevision(error) && onStale() },
        );
      }}
    >
      <div className="card-body stack-md">
        <h2 id="editor-fields-title">Fields, tags and experts</h2>
        <FormError error={isStaleRevision(update.error) ? null : bannerError(update.error, FIELD_PATHS)} />
        <AssetFieldsForm draft={draft} set={(next) => setDraft((d) => ({ ...d, ...next }))} current={asset} errors={serverFieldErrors(update.error)} />
        <SaveBar changed={changed} valid={fieldsValid(draft)} state={buttonState(update)} onReset={() => setDraft(initial)} />
      </div>
    </form>
  );
}

function Editor({ asset }: { asset: AssetOut }) {
  const reload = useAsset(asset.id);
  const [stale, setStale] = useState(false);
  return (
    <>
      <PageHead title={`Edit ${asset.name}`} subtitle="Each part saves on its own." back={{ to: `/assets/${asset.id}`, name: asset.name }} />
      {stale && (
        <StaleNotice
          reloading={reload.isFetching}
          onReload={() => void reload.refetch().then(() => setStale(false))}
        />
      )}
      <div className="asset-editor stack-md">
        <FieldsCard key={`fields-${asset.id}`} asset={asset} onStale={() => setStale(true)} />
        <SectionsEditor asset={asset} onStale={() => setStale(true)} />
        <RelationsEditor key={`relations-${asset.id}`} asset={asset} onStale={() => setStale(true)} />
      </div>
    </>
  );
}

export function AssetEditPage() {
  const { assetId = '' } = useParams();
  const { can } = useSession();
  const asset = useAsset(assetId);
  const back = { to: `/assets/${assetId}`, name: 'the asset' };

  if (!can('asset.manage')) {
    return (
      <>
        <PageHead title="Edit asset" back={back} />
        <StateCard icon={<Lock aria-hidden="true" />} title="You cannot edit this asset">
          <p>Asset managers edit the catalog.</p>
        </StateCard>
      </>
    );
  }
  if (asset.isPending) return <LoadingCard label="Loading the asset" />;
  if (asset.error) {
    return (
      <>
        <PageHead title="Edit asset" back={back} />
        <LoadFailure error={asset.error} onRetry={() => void asset.refetch()} />
      </>
    );
  }
  return <Editor asset={asset.data} />;
}
